import { CSSResultGroup, LitElement, html } from 'lit'
import { customElement, property, state, query } from 'lit/decorators.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { style } from './plp-filters.style.js'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import {
  FILTERS_SEARCH_FILTERS_API,
  FiltersSearchAPI,
  FiltersSearchRequest
} from './services/search-filters-api.js'
import {
  APIBrand,
  APIProperty,
  FilterChangeDetail,
  SelectedFilter,
  SearchFiltersResponse,
  TemplateCategory
} from 'components/discovery/plp/filters/plp-filters/typing/filters-typing.js'
import { FilterParams } from './services/filter-params.js'
import {
  FilterRemovedEvent,
  FiltersClearedEvent,
  PlpFiltersSelectedFiltersEvent,
  PlpFiltersFavoritesToggleEvent,
  PlpFiltersCheckboxChangeEvent,
  PlpFiltersRangeChangeEvent,
} from './events.js'
import { FavoritesChangeEvent } from 'components/discovery/plp/filters/favorites-filter/favorites-filter.js'
import { CheckboxChangeEvent, CheckboxRangeChangeEvent } from 'components/discovery/plp/filters/checkbox-filters/checkbox-filters.js'
import { initializeIconsByRole } from '@cloudsuite/mosaic-shared/icons/library.js'
import { HiddenFieldsMixin } from 'components/shared/hidden-fields/hidden-fields-mixin.js'
import 'components/discovery/plp/filters/favorites-filter/favorites-filter.js'
import 'components/discovery/plp/filters/category-filters/category-filters.js'
import 'components/discovery/plp/filters/checkbox-filters/checkbox-filters.js'

initializeIconsByRole({
  'csc-plp-filters.mobile-filter': { name: 'sliders', style: 'regular' }
})

const DEFAULT_HEADING_VARIANT = 'heading-4'
const DEFAULT_ACCORDION_SIZE = 'md'

export const plpFiltersHiddenFields = ['favorites', 'stock', 'count', 'brands'] as const

@customElement('csc-plp-filters')
export class CSCPlpFilters extends HiddenFieldsMixin(LitElement, plpFiltersHiddenFields, { hiddenByDefault: ['favorites', 'stock', 'count'] }) {
  static override styles: CSSResultGroup = [hostStyle, style]

  @property({ type: Boolean, attribute: 'favorites-checked' }) favoritesChecked = false
  /** @deprecated The favorites filter is shown unless `hidden-fields` contains `favorites`. */
  @property({ type: Boolean, attribute: 'show-favorites' })
  set showFavorites(value: boolean) {
    this.setLegacyVisibility('show-favorites', 'favorites', value)
  }

  get showFavorites() {
    return !this.isHidden('favorites')
  }
  @property({ type: String, attribute: 'search-endpoint' }) searchEndpoint = ''
  @property({ type: String, attribute: 'test-endpoint-url' }) testEndpointUrl = ''

  /** Mirrors csc-plp-grid: scoped PLP context for the filters API query string. */
  @property({ type: String, attribute: 'tree-id' })
  treeId?: string

  @property({ type: String, attribute: 'brand-id' })
  brandId?: string

  @property({ type: String, attribute: 'tag-id' })
  tagId?: string

  @property({ type: String, attribute: 'search-term' })
  searchTerm?: string

  /** @deprecated Categories are rendered from the filters API `trees`; only used until the API has responded. */
  @property({ type: Array }) categories: TemplateCategory[] = []
  @property({ type: Array, attribute: 'brands' }) brandRefinements: APIBrand[] = []
  @property({ type: Array, attribute: 'properties' }) propertyRefinements: APIProperty[] = []
  @property({ type: Array, attribute: 'selected-filters' }) selectedFilters: SelectedFilter[] = []
  @state() stockSelected = false
  @property({ type: String, attribute: 'heading-variant' }) headingVariant = DEFAULT_HEADING_VARIANT
  @property({ type: String, attribute: 'accordion-size' }) accordionSize = DEFAULT_ACCORDION_SIZE

  /** @deprecated Override the `csc-plp-filters.accordion-closed` and `csc-plp-filters.accordion-open` icon roles through the icon receiver instead. */
  @property({ type: String, attribute: 'icon-role' })
  set iconRole(_value: string) {
    console.warn('<csc-plp-filters> "icon-role" is deprecated, override the "csc-plp-filters.accordion-closed" and "csc-plp-filters.accordion-open" icon roles through the icon receiver instead.')
  }

  get iconRole() {
    return 'toggle'
  }

  /** @deprecated Use `accordion-size` instead. */
  @property({ type: String, attribute: 'category-size' })
  set categorySize(value: string) {
    console.warn('<csc-plp-filters> "category-size" is deprecated, use "accordion-size" instead.')
    this.accordionSize = value
  }

  get categorySize() {
    return this.accordionSize
  }

  /** @deprecated Set `--csc-category-filters-content-gap` in CSS instead. */
  @property({ type: String, attribute: 'category-gap' })
  set categoryGap(_value: string) {
    console.warn('<csc-plp-filters> "category-gap" is deprecated, set "--csc-category-filters-content-gap" in CSS instead.')
  }

  get categoryGap() {
    return ''
  }
  @property({ type: Number, attribute: 'brands-display-limit' }) brandsDisplayLimit = 5

  /** @deprecated Use `hidden-fields="brands"` instead. */
  @property({ type: Boolean, attribute: 'hide-brands' })
  set hideBrands(value: boolean) {
    this.setLegacyVisibility('hide-brands', 'brands', !value)
  }

  get hideBrands() {
    return this.isHidden('brands')
  }

  /** @deprecated The stock filter is shown unless `hidden-fields` contains `stock`. */
  @property({ type: Boolean, attribute: 'show-stock' })
  set showStock(value: boolean) {
    this.setLegacyVisibility('show-stock', 'stock', value)
  }

  get showStock() {
    return !this.isHidden('stock')
  }

  /** @deprecated The counts are shown unless `hidden-fields` contains `count`. */
  @property({ type: Boolean, attribute: 'display-count' })
  set displayCount(value: boolean) {
    this.setLegacyVisibility('display-count', 'count', value)
  }

  get displayCount() {
    return !this.isHidden('count')
  }

  /** @deprecated Use `brands-display-limit` instead. */
  @property({ type: Boolean, attribute: 'show-all-brands' })
  set showAllBrands(value: boolean) {
    console.warn('<csc-plp-filters> "show-all-brands" is deprecated, use "brands-display-limit" instead.')
    if (value) this.brandsDisplayLimit = Infinity
  }

  get showAllBrands() {
    return this.brandsDisplayLimit === Infinity
  }
  @state() isLoading = false
  @state() totalFound = 0
  @state() private apiCategories: TemplateCategory[] | null = null

  @query('cse-slide-in')
  slideIn!: any

  @inject(FILTERS_SEARCH_FILTERS_API)
  private searchService!: FiltersSearchAPI

  private filterParams: FilterParams | null = null

  /** Recreate FilterParams when the filters endpoint URL changes. */
  private filterParamsEndpointKey = ''

  private getFilterParams(): FilterParams {
    const key = `${this.searchEndpoint}\0${this.testEndpointUrl}`
    if (!this.filterParams || this.filterParamsEndpointKey !== key) {
      this.filterParams = new FilterParams(this.searchEndpoint, this.testEndpointUrl)
      this.filterParamsEndpointKey = key
    }
    return this.filterParams
  }

  /** Merge tree / brand / tag / search props into params before build (parity with grid). */
  private syncGridContextOntoFilterParams(): void {
    this.getFilterParams().applyContextFromProps({
      tree_id: this.treeId,
      brand_id: this.brandId,
      tag_id: this.tagId,
      search_term: this.searchTerm,
    })
  }

  override connectedCallback(): void {
    super.connectedCallback()
    document.addEventListener(FilterRemovedEvent.type, this.handleFilterRemovedEvent)
    document.addEventListener(FiltersClearedEvent.type, this.handleFiltersClearedEvent)
    if (typeof window !== 'undefined') {
      window.addEventListener('popstate', this.handlePopState)
    }
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback()
    document.removeEventListener(FilterRemovedEvent.type, this.handleFilterRemovedEvent)
    document.removeEventListener(FiltersClearedEvent.type, this.handleFiltersClearedEvent)
    if (typeof window !== 'undefined') {
      window.removeEventListener('popstate', this.handlePopState)
    }
  }

  override firstUpdated(): void {
    if (typeof window !== 'undefined') {
      this.getFilterParams().applyRefinementsFromUrl(window.location.href)
    }
    if (this.searchEndpoint) {
      this.fetchFilters()
    }
  }

  private handlePopState = (): void => {
    if (!this.searchEndpoint) return
    this.getFilterParams().applyRefinementsFromUrl(window.location.href)
    this.refetchFilters()
  }

  private updateBrowserUrl(): void {
    if (typeof window === 'undefined') return
    const currentParams = new URLSearchParams(window.location.search)
    const refinementParams = new URLSearchParams(this.getFilterParams().getRefinementParamsString())
    const refinementValues = refinementParams.getAll('v')

    // Preserve existing context params (search_term/tree_id/brand_id/tag_id, etc.)
    // and only replace active refinement values.
    currentParams.delete('v')
    refinementValues.forEach((value) => currentParams.append('v', value))

    const newSearch = currentParams.toString()
    const newQuery = newSearch ? `?${newSearch}` : ''
    const currentQuery = window.location.search || ''
    if (currentQuery !== newQuery) {
      window.history.pushState(null, '', window.location.pathname + newQuery)
    }
  }

  private async fetchFilters(): Promise<void> {
    const url = this.buildUrlWithParams()

    if (!url) return

    this.isLoading = true
    try {
      const request = new FiltersSearchRequest(url.toString(), this.testEndpointUrl || undefined)
      const response = await this.searchService.fetchFilters(request)
      
      if (response) {
        this.applyResponse(response)
      }
    } catch (error) {
      console.error('Failed to fetch PLP filters:', error)
    } finally {
      this.isLoading = false
    }
  }

  private buildUrlWithParams(): URL | null {
    if (!this.searchEndpoint) return null
    this.syncGridContextOntoFilterParams()
    return this.getFilterParams().buildUrl()
  }

  private applyResponse(response: SearchFiltersResponse): void {
    this.favoritesChecked = response.search_filters?.is_favourite
    this.stockSelected = response.search_filters?.in_stock
    this.brandRefinements = this.isHidden('brands') ? [] : response.brands
    this.propertyRefinements = response.attributes
    this.apiCategories = response.trees.map(({ page_link, ...tree }) => ({ ...tree, url: page_link }))
    this.selectedFilters = response.selected_filters
    this.totalFound = response.total_found
    this.dispatchEvent(new PlpFiltersSelectedFiltersEvent(
      this.selectedFilters,
      this.buildUrlWithParams()?.toString() || ''
    ))
    this.updateBrowserUrl()
  }

  private refetchFilters(): void {
    if (this.searchEndpoint) {
      this.fetchFilters()
    }
  }

  applySelectedFilterUrl(url: string): void {
    if (!url) return
    this.getFilterParams().applyFromUrl(url)
    this.updateBrowserUrl()
    this.refetchFilters()
  }

  clearSelectedFilters(): void {
    this.getFilterParams().clearSelectedFilters()
    this.updateBrowserUrl()
    this.refetchFilters()
  }

  private handleFilterRemovedEvent = (event: FilterRemovedEvent): void => {
    if (event.filter.api_link) {
      this.applySelectedFilterUrl(event.filter.api_link)
    }
  }

  private handleFiltersClearedEvent = (): void => {
    this.clearSelectedFilters()
  }

  private handleFavoritesToggle = (event: FavoritesChangeEvent): void => {
    event.stopPropagation()
    this.getFilterParams().setFavorites(event.checked)
    this.updateBrowserUrl()
    this.refetchFilters()
    this.dispatchEvent(new PlpFiltersFavoritesToggleEvent(
      event.checked,
      this.buildUrlWithParams()?.toString() || ''
    ))
  }

  private handleCheckboxChange = (event: CheckboxChangeEvent): void => {
    event.stopPropagation()
    this.updateFilterParams(event.change)
    this.updateBrowserUrl()
    this.refetchFilters()
    this.dispatchEvent(new PlpFiltersCheckboxChangeEvent(
      event.change,
      this.buildUrlWithParams()?.toString() || ''
    ))
  }

  private handleRangeChange = (event: CheckboxRangeChangeEvent): void => {
    event.stopPropagation()
    const { action, minValue, maxValue, filter } = event.change
    const params = this.getFilterParams()

    if (action === 'filter') {
      params.setRange(filter.id, minValue, maxValue)
    } else if (action === 'clear') {
      params.clearRange(filter.id)
    }

    this.updateBrowserUrl()
    this.refetchFilters()
    this.dispatchEvent(new PlpFiltersRangeChangeEvent(
      event.change,
      this.buildUrlWithParams()?.toString() || ''
    ))
  }

  private updateFilterParams(detail: FilterChangeDetail): void {
    const params = this.getFilterParams()
    switch (detail.type) {
      case 'brand':
        params.toggleBrand(detail.id, detail.selected)
        break
      case 'property':
        params.toggleProperty(detail.propertyId, detail.valueId, detail.selected)
        break
      case 'stock':
        params.setStock(detail.selected)
        break
    }
  }

  private renderFavoritesFilter() {
    return html`
      <csc-favorites-filter
        .checked=${this.favoritesChecked}
        @favorites-change=${this.handleFavoritesToggle}
      ></csc-favorites-filter>
    `
  }

  private renderCategoryFilters() {
    const categories = this.apiCategories ?? this.categories
    if (!Array.isArray(categories) || !categories.length) return null

    return html`
      <csc-category-filters
        .hiddenFields=${this.childHiddenFields(['count'])}
        .categories=${categories}
        .headingVariant=${this.headingVariant}
        .accordionSize=${this.accordionSize}
      ></csc-category-filters>
    `
  }

  private renderCheckboxFilters() {
    const brands = this.isHidden('brands') ? [] : this.brandRefinements
    if (!brands.length && !this.propertyRefinements.length) {
      return null
    }

    return html`
      <csc-checkbox-filters
        .hiddenFields=${this.childHiddenFields(['count', 'stock'])}
        .brandsDisplayLimit=${this.brandsDisplayLimit}
        .brandRefinements=${brands}
        .propertyRefinements=${this.propertyRefinements}
        .stockSelected=${this.stockSelected}
        .headingVariant=${this.headingVariant}
        .accordionSize=${this.accordionSize}
        @checkbox-change=${this.handleCheckboxChange}
        @checkbox-range-change=${this.handleRangeChange}
      ></csc-checkbox-filters>
    `
  }


  private childHiddenFields<F extends 'count' | 'stock'>(fields: F[]): Set<F> {
    return new Set(fields.filter(field => this.isHidden(field)))
  }

  private openMobileFilters = () => {
    this.slideIn?.open()
  }

  private closeMobileFilters = () => {
    this.slideIn?.close()
  }

  private renderMobileButton() {
    return html`
      <div class="mobile-filters-button">
        <cse-button @click=${this.openMobileFilters} variant="secondary" fit="fill">
          <cse-icon icon-role="csc-plp-filters.mobile-filter" slot="prefix"></cse-icon>
          ${t('filters', { ns: 'csc-plp-filters', default: 'Filters' })} 
          <cse-badge slot="suffix"  size="small" composition="floating" >${this.selectedFilters?.length ?? 0}</cse-badge>
        </cse-button>
      </div>
    `
  }

  private renderFiltersContent() {
    return html`
      ${!this.isHidden('favorites') ? this.renderFavoritesFilter() : null}
      ${!this.isHidden('favorites') ? html`<cse-divider orientation="horizontal" variant="solid"></cse-divider>` : null}
      ${this.renderCategoryFilters()}
      ${this.renderCheckboxFilters()}
    `
  }

  private renderMobileSlideIn() {
    return html`
      <cse-slide-in>
        <div slot="header">
          <cse-text variant="heading-3">Filters</cse-text>
        </div>
        <div class="mobile-filters-content">
          <div class="mobile-filters-container">
            ${this.isLoading ? html`<div class="loading-overlay"></div>` : null}
            <csc-active-filters hidden-fields="filter-name"></csc-active-filters>
            ${this.selectedFilters?.length ? html`<cse-divider orientation="horizontal" variant="solid"></cse-divider>` : null}
            ${this.renderFiltersContent()}
          </div>
        </div>
          <cse-button slot="footer" @click=${this.closeMobileFilters} variant="primary" fit="fill">
          ${t('show_results', { ns: 'csc-plp-filters', default: 'Show results' })} (${this.totalFound})
          </cse-button>
      </cse-slide-in>
    `
  }

  private renderSkeleton() {
    return html`
      <div class="plp-filters-skeleton">
        <cse-skeleton height="24" index="0" variant="secondary"></cse-skeleton>
        <cse-skeleton height="18" index="1" variant="secondary"></cse-skeleton>
        <cse-skeleton height="24" index="2" variant="secondary"></cse-skeleton>
        <cse-skeleton height="18" index="3" variant="secondary"></cse-skeleton>
        <cse-skeleton height="18" index="4" variant="secondary"></cse-skeleton>
      </div>
    `
  }

  override render() {
    if (this.isLoading && !this.brandRefinements.length && !this.propertyRefinements.length) {
      return this.renderSkeleton()
    }

    return html`
      <div class="plp-filters-container">
        ${this.isLoading ? html`<div class="loading-overlay"></div>` : null}
        
        <div class="mobile-view">
          ${this.renderMobileButton()}
          ${this.renderMobileSlideIn()}
        </div>
        
        <div class="desktop-view">
          <div class="plp-filters">
            ${this.renderFiltersContent()}
          </div>
        </div>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-plp-filters': CSCPlpFilters
  }
}
